import { containsHtmlFragment } from './containsHtmlFragment';
import { sendFeedback } from './sendFeedback';

/** Thrown when a feedback submission cannot be sent. The message is safe to display to users. */
export class FeedbackSubmissionError extends Error {}

export interface SendFeedbackOptions {
  /** Recipient flag key. Feedback is tied to this flag in the qualitative feedback product. */
  flagKey: string;
  feedback: string;
  sentiment: 'positive' | 'negative' | null;
  /** Extra context attached to the feedback item (e.g. the submitter's email when they opt in to follow-up). */
  customProperties?: Record<string, unknown>;
}

/**
 * Submits feedback to the qualitative feedback product via the SDK, tied to `flagKey`.
 *
 * Throws `FeedbackSubmissionError` (with a user-facing message) when the feedback
 * is rejected client-side. Network/runtime errors propagate as-is.
 */
export async function sendFeedbackToDestinations({
  flagKey,
  feedback,
  sentiment,
  customProperties,
}: SendFeedbackOptions): Promise<void> {
  // Drop submissions that contain HTML/markup tag pairs before they reach the
  // destination. This is a client-side noise filter (scanners, accidental
  // markup dumps), not a security boundary — see containsHtmlFragment.
  if (containsHtmlFragment(feedback)) {
    throw new FeedbackSubmissionError('Please remove any HTML or code tags from your feedback before submitting.');
  }

  await sendFeedback({ flagKey, feedback, sentiment, customProperties });
}
